import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import type { CrmPqrPendienteItem } from "@/lib/api";

interface Props {
  itemsBandeja: CrmPqrPendienteItem[];
}

const DIAS = [
  "domingo",
  "lunes",
  "martes",
  "miércoles",
  "jueves",
  "viernes",
  "sábado",
];

const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

function saludo(hora: number): string {
  if (hora < 12) return "Buenos días";
  if (hora < 19) return "Buenas tardes";
  return "Buenas noches";
}

function fechaLegible(d: Date): string {
  const dia = DIAS[d.getDay()];
  const num = d.getDate();
  const mes = MESES[d.getMonth()];
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  return `${dia} ${num} de ${mes} · ${hh}:${mm}`;
}

function nombreCorto(sub: string): string {
  // "ana.gomez" -> "Ana", "carlos.guzman" -> "Carlos".
  const head = sub.split(/[.@_-]/)[0];
  if (!head) return "";
  return head.charAt(0).toUpperCase() + head.slice(1).toLowerCase();
}

/**
 * Hero personalizado del analista (UI-2).
 *
 * Saludo + 3 KPIs personales calculados client-side desde la bandeja:
 * - Total en bandeja (excluye cerrados).
 * - Vencen hoy (días hábiles <= 0 o fecha de vencimiento <= hoy).
 * - Confianza baja (< 0.70).
 *
 * Atajos: "Importar desde CRM" (refresh) y "Nuevo expediente".
 */
export function HeroAnalista({ itemsBandeja }: Props) {
  const { sesion } = useAuth();
  if (!sesion || sesion.rol === "sistema") return null;

  const now = new Date();
  const nombre = nombreCorto(sesion.sub);
  const hello = saludo(now.getHours());

  // KPIs calculados client-side. Una iteración futura los traerá del
  // backend con asignación por analista — por ahora son del total de
  // la bandeja para que el operador tenga una sensación clara.
  const enBandeja = itemsBandeja.filter(
    (it) =>
      it.estado_expediente !== "APROBADO" &&
      it.estado_expediente !== "RECHAZADO" &&
      it.estado_expediente !== "NOTIFICADO" &&
      it.estado_expediente !== "CERRADO_APROBADO",
  );
  const vencenHoy = enBandeja.filter((it) => {
    const dh = it.dias_habiles;
    if (dh != null && dh <= 0) return true;
    if (!it.fecha_vencimiento) return false;
    const venc = new Date(it.fecha_vencimiento);
    const hoy = new Date();
    return (
      venc.getFullYear() === hoy.getFullYear() &&
      venc.getMonth() === hoy.getMonth() &&
      venc.getDate() === hoy.getDate()
    );
  }).length;
  const confianzaBaja = enBandeja.filter(
    (it) => (it.confianza_motor ?? 1) < 0.7 && it.confianza_motor != null,
  ).length;

  return (
    <div
      className="card"
      style={{
        marginBottom: 16,
        background: "var(--surface)",
        borderColor: "var(--border)",
      }}
    >
      <div
        style={{
          padding: "20px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              className="eyebrow"
              style={{ marginBottom: 6 }}
            >
              {fechaLegible(now)}
            </div>
            <h1
              style={{
                margin: 0,
                fontFamily: "var(--font-serif, 'IBM Plex Serif', serif)",
                fontWeight: 500,
                fontSize: 26,
                color: "var(--ink)",
                lineHeight: 1.2,
              }}
            >
              {hello}{nombre ? `, ${nombre}.` : "."}
            </h1>
          </div>
          <div className="row row-2">
            <Link to="/expedientes/nuevo" className="btn btn--accent">
              Nuevo expediente
            </Link>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 18,
            alignItems: "center",
            fontSize: 14,
            color: "var(--ink-2)",
            flexWrap: "wrap",
          }}
        >
          <KpiInline numero={enBandeja.length} label="expedientes en tu bandeja" />
          <span className="muted">·</span>
          <KpiInline
            numero={vencenHoy}
            label="vencen hoy"
            color={vencenHoy > 0 ? "var(--red, oklch(0.55 0.18 25))" : undefined}
          />
          <span className="muted">·</span>
          <KpiInline
            numero={confianzaBaja}
            label="con confianza baja"
            color={
              confianzaBaja > 0 ? "var(--amber-deep, oklch(0.55 0.15 75))" : undefined
            }
          />
        </div>
      </div>
    </div>
  );
}

function KpiInline({
  numero,
  label,
  color,
}: {
  numero: number;
  label: string;
  color?: string;
}) {
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
      <strong
        style={{
          fontSize: 20,
          color: color ?? "var(--ink)",
          fontWeight: 600,
        }}
      >
        {numero}
      </strong>
      <span>{label}</span>
    </span>
  );
}
